/* Copyright (c) 2020-present CloudIO Inc */

import { toolTipProps } from 'componentregistry/utils/commonProps';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './IconCase';

const IconCaseManager: ComponentManagerFn<'IconCase'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'IconCase'>(platform, 'IconCase'),
    type: 'IconCase',
    name: 'Icon Case',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      caseValue: {
        label: 'Case Value',
        type: 'string',
        helpText:
          'Provide a constant string value (without quotes) for this case to match the switch expression of the parent Icon Switch component. An empty case will be matched with any switch expression.',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.icon?.iconName ?? 'Choose icon!',
    category: 'Forms',
    noOverlay: true,
    excludeChildren: true,
    icon: (item) => {
      if (item.props.icon?.iconName) {
        return item.props.icon;
      }
      return { iconName: 'icons' };
    },
    description: `**Overview**<br/>
The Icon Case component is a conditional building block designed exclusively for use within the **Icon Switch** component. It functions exactly like a "case" in a programming switch statement: it defines a specific value to match and the visual icon to display when that match occurs.
Use this component to define the individual states of a dynamic icon system. For example, in a "Status" icon switch, you would have one Icon Case for "Approved" (Green Check), one for "Pending" (Yellow Clock), and one for "Rejected" (Red X).

**1. Logic & Matching**<br/>
The parent **Icon Switch** evaluates an expression (the "Switch Expression"). The Icon Case provides the value to compare against that result.
* **Case Value:** A constant string value.
* **Logic:** If the parent's Switch Expression evaluates to this string, this Icon Case becomes active.
* **Formatting:** Do not use quotes (e.g., enter **Approved**, not **'Approved'**).
* **Default Case:** If you leave **Case Value** empty, this component acts as the "default" or "fallback" case, matching any value that hasn't been caught by previous cases.

**2. Visual Configuration**<br/>
When an Icon Case is matched, its properties determine what is rendered on the screen.
* **Icon:** Select the visual symbol to display (e.g., check, clock, times).
* **Color:** Defines the color of the icon when this case is active.
* **Supported Values:** Theme colors like **primary**, **secondary**, **error**, **success**.
* **Tooltip:** Inherits standard tooltip properties. You can define specific hover text for this state (e.g., "This item has been approved").

**3. Interactivity**<br/>
Even though it is part of a larger switch logic, individual cases can trigger specific actions.
* **TriggerOnClick:** You can attach a trigger to this specific case.
* **Use Case:** Clicking the "Rejected" icon could open a modal showing the rejection reason, while clicking the "Approved" icon does nothing.

**4. Best Use Cases**<br/>
* **Status Indicators:** Visualizing workflow states (Draft, Review, Published).
* **Priority Levels:** Showing different icons/colors for High (Red Exclamation), Medium (Orange Dash), and Low (Green Down Arrow) priority tasks.
* **File Types:** Displaying different icons based on a file extension (PDF icon for .pdf, Word icon for .docx).
* **Boolean Toggles:** A simple switch with two cases: "True" (Checkmark) and "False" (Cross).`,
  };
};

export default IconCaseManager;
